/**
 * Что можно сделать с макетом на его текущей стадии конвейера.
 *
 * Единственный источник правды для полосы «Что дальше» в карточке макета:
 * стадия решает, какое действие главное, какие рядом и что произойдёт после
 * нажатия. Раньше карточка знала только про «Одобрить» и «Перегенерировать»,
 * причём обе кнопки жили внутри вкладки воронок и пропадали, когда воронок
 * ещё не было, — то есть ровно тогда, когда одобрение и требовалось.
 *
 * Сценарии — docs/DESIGN_USER_SCENARIOS.md (C3–C8, D21–D22, E1–E7).
 */

export type StageActionKind =
  | 'accept'
  | 'return'
  | 'techCheck'
  | 'regenerate'
  | 'regenerateProduction'
  | 'retry'
  | 'createProducts'
  | 'recalcKit'

export interface StageAction {
  kind: StageActionKind
  label: string
  /** Заголовок и текст подтверждения; без него действие выполняется сразу */
  confirm?: { title: string; message: (designCode: string, ctx: StageContext) => string }
  /** Требует комментарий (возврат дизайнеру) */
  requiresComment?: boolean
  variant?: 'primary' | 'secondary' | 'danger'
}

export interface StageContext {
  /** Сколько товаров 1С ожидается по матрице типов */
  expectedProducts: number
  /** Сколько уже создано */
  createdProducts: number
  /** Живых карточек маркетплейсов */
  cards: number
}

export interface StagePlan {
  /** Короткое «где мы сейчас» человеческим языком */
  headline: string
  /** Что произойдёт по главной кнопке */
  hint: (ctx: StageContext) => string
  primary?: StageAction
  secondary: StageAction[]
  /** Тон полосы: обычная работа, ожидание решения, проблема */
  tone: 'neutral' | 'decision' | 'problem'
}

const ACCEPT: StageAction = {
  kind: 'accept',
  label: 'Принять макет',
  variant: 'primary',
  confirm: {
    title: 'Принять макет?',
    message: (code, ctx) =>
      `Макет #${code} уйдёт в работу: создадутся товары 1С` +
      (ctx.expectedProducts ? ` (${ctx.expectedProducts})` : '') +
      ' и файлы для печати. Визуалы и карточки маркетплейсов приёмка не создаёт — ' +
      'для них нужен выбор кабинетов. Отменить это одной кнопкой нельзя.\n\n' +
      // Категории без опубликованных контент-воронок остаются производственными.
      // Раньше такая категория роняла приёмку целиком, теперь молча проходит —
      // человек должен знать об этом до нажатия, а список конкретных категорий
      // приходит в ответе приёмки.
      'Категории без опубликованной контент-воронки карточку ' +
      'не получат — они идут как производственные позиции. Какие именно ' +
      'пропущены, покажет уведомление после приёмки.',
  },
}

const RETURN: StageAction = {
  kind: 'return',
  label: 'Вернуть дизайнеру',
  variant: 'secondary',
  requiresComment: true,
}

const TECH_CHECK: StageAction = {
  kind: 'techCheck',
  label: 'Проверить файлы',
  variant: 'secondary',
}

const REGENERATE: StageAction = {
  kind: 'regenerate',
  label: 'Перегенерировать визуалы',
  variant: 'secondary',
  confirm: {
    title: 'Перегенерировать визуалы?',
    message: (code) =>
      `Визуалы всех воронок макета #${code} будут созданы заново и перезапишут текущие. ` +
      'Файлы печати не тронем — они переиспользуются как есть. Если по какой-то ' +
      'категории файла печати нет, кнопка честно откажет: сначала перегенерируйте ' +
      'файлы для печати.',
  },
}

/**
 * Вторая половина работы прогона, отдельной кнопкой.
 *
 * Раньше «Перегенерировать визуалы» пересоздавала и файлы печати — минуты
 * работы движка и лишняя запись в хранилище макетов ради байт-в-байт того же
 * файла. Теперь каждая кнопка делает ровно то, что написано на ней.
 */
const REGENERATE_PRODUCTION: StageAction = {
  kind: 'regenerateProduction',
  label: 'Перегенерировать файлы для печати',
  variant: 'secondary',
  confirm: {
    title: 'Перегенерировать файлы для печати?',
    message: (code) =>
      `Файлы печати всех категорий макета #${code} будут созданы заново и ` +
      'перезапишут текущие в хранилище макетов. Визуалы, карточки маркетплейсов ' +
      'и стадия макета останутся как есть.',
  },
}

const RETRY: StageAction = {
  kind: 'retry',
  label: 'Повторить шаг',
  variant: 'primary',
  confirm: {
    title: 'Повторить упавший шаг?',
    message: (code) => `Конвейер повторит последний неудачный шаг макета #${code}.`,
  },
}

const CREATE_PRODUCTS: StageAction = {
  kind: 'createProducts',
  label: 'Создать товары',
  variant: 'secondary',
  confirm: {
    title: 'Создать товары по макету?',
    message: (code, ctx) =>
      `По макету #${code} будут созданы недостающие товары 1С` +
      (ctx.expectedProducts ? ` (ожидается ${ctx.expectedProducts})` : '') +
      '.',
  },
}

const RECALC_KIT: StageAction = {
  kind: 'recalcKit',
  label: 'Пересчитать комплект',
  variant: 'secondary',
}

const plural = (n: number, one: string, few: string, many: string) => {
  const abs = Math.abs(n) % 100
  const last = abs % 10
  if (abs > 10 && abs < 20) return many
  if (last > 1 && last < 5) return few
  if (last === 1) return one
  return many
}

const STAGE_PLANS: Record<string, StagePlan> = {
  on_acceptance: {
    headline: 'Ждёт вашей приёмки',
    tone: 'decision',
    hint: (ctx) =>
      ctx.expectedProducts
        ? `После приёмки создастся ${ctx.expectedProducts} ${plural(ctx.expectedProducts, 'товар', 'товара', 'товаров')} 1С и файлы для печати`
        : 'После приёмки создадутся товары 1С и файлы для печати',
    primary: ACCEPT,
    secondary: [RETURN, TECH_CHECK],
  },
  returned_to_designer: {
    headline: 'Возвращён дизайнеру',
    tone: 'problem',
    hint: () => 'Дизайнер исправляет макет. Когда пришлёт заново — примите его здесь же',
    primary: undefined,
    secondary: [ACCEPT],
  },
  // Сюда приёмка приводит макет в режиме «Макеты и товары»: заказаны товары и
  // файлы для печати, визуалы не заказывались вовсе. Прежний generating
  // («Визуалы генерируются») заставлял ждать картинок, которых не будет.
  products_created: {
    headline: 'Товары созданы, визуалов ещё нет',
    tone: 'neutral',
    hint: () =>
      'Приёмка сделала товары и файлы для печати. Визуалы по воронкам маркетплейсов запустит эта кнопка',
    primary: { ...REGENERATE, label: 'Отправить в генерацию', variant: 'primary' },
    secondary: [REGENERATE_PRODUCTION, RECALC_KIT],
  },
  awaiting_media: {
    headline: 'Ждёт генерации визуалов',
    tone: 'neutral',
    hint: () => 'Запустит генерацию визуалов по воронкам маркетплейсов',
    primary: { ...REGENERATE, label: 'Отправить в генерацию', variant: 'primary' },
    secondary: [REGENERATE_PRODUCTION, RECALC_KIT],
  },
  generating: {
    headline: 'Визуалы генерируются',
    tone: 'neutral',
    hint: (ctx) =>
      ctx.expectedProducts
        ? `Движок работает по ${ctx.expectedProducts} ${plural(ctx.expectedProducts, 'товару', 'товарам', 'товарам')} — обновите карточку через минуту`
        : 'Движок работает — обновите карточку через минуту',
    primary: undefined,
    secondary: [REGENERATE],
  },
  media_ready: {
    headline: 'Визуалы готовы',
    tone: 'neutral',
    hint: () => 'Осталось дождаться сборки карточек маркетплейсов',
    primary: undefined,
    secondary: [REGENERATE, REGENERATE_PRODUCTION],
  },
  // Кнопки «Одобрить публикацию» здесь нет (04.10.2026): карточки выкладываются из «Черновиков ПИМ» в каталогах площадок.
  on_approval: {
    headline: 'Черновики готовы',
    tone: 'neutral',
    hint: () => 'Черновики карточек — на вкладке «Карточки МП». Выкладка на площадки — из «Черновиков ПИМ» в каталогах WB, OZON и Яндекс Маркета',
    primary: undefined,
    secondary: [REGENERATE, REGENERATE_PRODUCTION],
  },
  publishing: {
    headline: 'Публикуется на маркетплейсах',
    tone: 'neutral',
    hint: () =>
      'Карточки создаются в кабинетах. Коммерческие значения останутся только в PIM: внешняя отправка цен и остатков не подключена',
    primary: undefined,
    secondary: [],
  },
  // Файлы печати наружу не уходят, поэтому пересоздать их можно и у макета,
  // который уже продаётся: это ровно тот сценарий, ради которого делалось
  // дозаполнение печати по хранилищу.
  awaiting_sales_start: {
    headline: 'Коммерческие значения рассчитаны в PIM',
    tone: 'neutral',
    hint: () =>
      'Цена и плановый остаток сохранены только в PIM; внешняя отправка на маркетплейсы не подключена',
    primary: undefined,
    secondary: [REGENERATE_PRODUCTION],
  },
  selling: {
    headline: 'Историческая отметка PIM',
    tone: 'neutral',
    hint: () => 'Состояние карточки на маркетплейсе не проверено',
    primary: undefined,
    secondary: [REGENERATE_PRODUCTION],
  },
  error: {
    headline: 'Конвейер остановился на ошибке',
    tone: 'problem',
    hint: () => 'Устраните указанную ниже причину и нажмите «Повторить шаг»',
    primary: RETRY,
    secondary: [RETURN, REGENERATE_PRODUCTION],
  },
  imported: {
    headline: 'В каталоге, в конвейер не заходил',
    tone: 'neutral',
    hint: (ctx) =>
      ctx.createdProducts >= ctx.expectedProducts && ctx.expectedProducts > 0
        ? 'Комплект товаров собран'
        : 'Создаст товары 1С по матрице типов макетов',
    primary: undefined,
    secondary: [CREATE_PRODUCTS, RECALC_KIT],
  },
  draft: {
    headline: 'Черновик',
    tone: 'neutral',
    hint: () => 'Отправьте макет на приёмку, когда он будет готов',
    primary: undefined,
    secondary: [],
  },
}

/**
 * Стадии, на которых карточка макета могла уже уехать на площадку. Конвейерный
 * статус здесь врёт чаще всего: после бэкфилла 04.07 тысячи макетов с живой
 * витриной стоят в on_approval, и полоса звала выкладывать уже выложенное.
 */
const STAGES_AFTER_DRAFTS = new Set(['on_approval', 'publishing', 'awaiting_sales_start', 'selling'])

/**
 * Что полоса говорит, когда состояние макета (design_state) знает больше стадии:
 * живая карточка в зеркале каталога площадок — факт, отметка без неё — нет.
 * Действия остаются от стадии, меняется только текст.
 */
const STATE_TEXT: Record<string, Pick<StagePlan, 'headline' | 'hint'>> = {
  selling: {
    headline: 'Есть на площадке',
    hint: () => 'Живая карточка найдена в каталоге WB или OZON. Продажи и остатки PIM не проверяет',
  },
  marked_no_card: {
    headline: 'Отметка без карточки',
    hint: () => 'В конвейере стоит отметка PIM, но живой карточки на WB и OZON не найдено — состояние не проверено',
  },
}

/** План действий для стадии; для незнакомой стадии — только безопасные действия. */
export function stagePlan(pipelineStatus?: string | null, designState?: string | null): StagePlan {
  const known = pipelineStatus ? STAGE_PLANS[pipelineStatus] : undefined
  const plan: StagePlan = known || {
    headline: 'Стадия конвейера не определена',
    tone: 'neutral',
    hint: () => 'Доступны только действия, которые ничего не публикуют',
    primary: undefined,
    secondary: [RECALC_KIT],
  }
  const stateText = designState ? STATE_TEXT[designState] : undefined
  if (stateText && pipelineStatus && STAGES_AFTER_DRAFTS.has(pipelineStatus)) {
    return { ...plan, ...stateText }
  }
  return plan
}

/**
 * Отдельные действия, которые окно запускает не из полосы «Что дальше», а из
 * вкладок и меню «Ещё»: те же подтверждения и тот же путь выполнения.
 */
export const STANDALONE_ACTIONS: Record<
  'createProducts' | 'regenerateProduction' | 'regenerate',
  StageAction
> = {
  createProducts: CREATE_PRODUCTS,
  regenerateProduction: REGENERATE_PRODUCTION,
  regenerate: REGENERATE,
}
